import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { getPasswordValidationErrorKey } from "../../../utils/passwordValidation";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('customers');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: "",
    email: "",
    language: "",
  });
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Fetch order details
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await adminApi.call(`/customer/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.fetchError'));
          return;
        }

        setFormData({
          name: response.data?.name || "",
          phone: response.data?.phone || "",
          password: "",
          email: response.data?.email || "",
          language: response.data?.language || "",
        });
      } catch (error: any) {
        showErrorToast(t('messages.fetchError') + ": " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: name.includes('Id') ? (value ? Number(value) : null) : value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (formData.password.trim()) {
      const passwordValidationErrorKey = getPasswordValidationErrorKey(formData.password, formData.phone);
      if (passwordValidationErrorKey) {
        newErrors.password = t(`validation.${passwordValidationErrorKey}`);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit updated order
  const updateProfile = async () => {
    if (!validateForm()) {
      showErrorToast(t('validation.fillAllFields'));
      return;
    }

    setUpdating(true);

    try {
      const payload = {
        id: Number(id),
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        email: formData.email,
        language: formData.language,
      };
      const response = await adminApi.call('/customer/update', 'POST', payload);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));
      // setTimeout(() => navigate("/customers"), 2000); // Redirect after success
    } catch (error: any) {
      showErrorToast(t('messages.updateError') + ": " + error.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-4">{t('messages.loading')}</div>;
  }

  const breadcrumbs = [
    { label: t('breadcrumbs.customers'), path: '/customers' },
    { label: t('breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={`${t('editCustomer')} - ${formData.name} #${id}`}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.name')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('placeholders.name')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.email')}
            </label>
            <input
              type="email"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('placeholders.email')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.phone')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('placeholders.phone')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.language', 'Language')}
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="language"
              value={formData.language || ""}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('placeholders.language', 'Select Language')}</option>
              <option value="en">{t('common.english', 'English')}</option>
              <option value="ar">{t('common.arabic', 'Arabic')}</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.password')}
            </label>
            <input
              type="password"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.password ? 'is-invalid' : ''}`}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('placeholders.password')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/customers')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={updateProfile}
            loading={updating}
            disabled={updating}
          >
            {updating ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
